/**
 * Finite-difference European option pricer: Crank-Nicolson in log-price,
 * with Rannacher start-up.
 *
 * In x = ln S and time to expiry tau = T - t, Black-Scholes becomes
 *
 *   V_tau = (sigma^2 / 2) V_xx + mu V_x - r V,   mu = r - q - sigma^2 / 2,
 *
 * a convection-diffusion equation with constant coefficients (and, after
 * one more change of variables, the plain heat equation). The diffusion
 * term is the same implicit solve the cloud layer runs every frame
 * (stableFluids2d.ts, diffuse), here done exactly with a tridiagonal
 * solver instead of Jacobi iterations.
 *
 * Scheme: theta-method on a uniform grid,
 *   (I - theta dt A) V^{n+1} = (I + (1 - theta) dt A) V^n,
 * A the central-difference operator. theta = 1/2 (Crank-Nicolson) is
 * second order in dt and dx but rings at the payoff kink; the first
 * `rannacherSteps` steps are replaced by twice as many fully implicit
 * (theta = 1) half-steps, which damp the high-frequency error.
 *
 * Payoff smoothing: the payoff has a kink at the strike. Sampling it at
 * nodes makes the error depend on where the strike falls between nodes, so
 * refining the grid does not reduce error smoothly (measured: a 2x
 * refinement made the error larger). The cell containing the strike
 * instead gets the exact cell average of the payoff (Pooley, Vetzal, and
 * Forsyth 2003), which restores second-order convergence.
 *
 * Grid: centered on ln(spot) with spot on the middle node (nSpace odd), so
 * the answer is read directly without interpolation. Width = +-widthStdDevs
 * standard deviations of ln S at expiry. Dirichlet boundaries from the
 * asymptotic prices.
 *
 * Sources:
 * - Crank, J. and Nicolson, P. (1947). A practical method for numerical
 *   evaluation of solutions of partial differential equations of the
 *   heat-conduction type. Proc. Cambridge Philosophical Society, 43(1),
 *   50-67. doi:10.1017/S0305004100023197
 * - Rannacher, R. (1984). Finite element solution of diffusion problems
 *   with irregular data. Numerische Mathematik, 43, 309-327.
 *   doi:10.1007/BF01390130
 * - Pooley, D. M., Vetzal, K. R., and Forsyth, P. A. (2003). Convergence
 *   remedies for non-smooth payoffs in option pricing. Journal of
 *   Computational Finance, 6(4), 25-40. doi:10.21314/jcf.2003.101
 * - Thomas algorithm: standard Gaussian elimination for tridiagonal systems.
 */
import type { BsInputs } from './blackScholes';

export interface FdGridOptions {
  /** Spatial nodes in log-price. Must be odd (spot on the middle node). */
  nSpace: number;
  /** Time steps. */
  nTime: number;
  /** Half-width of the log-price domain in standard deviations. */
  widthStdDevs: number;
  /** Crank-Nicolson steps replaced by implicit half-steps (Rannacher). */
  rannacherSteps: number;
  /** Cell-average the payoff in the strike cell (default true). Off only to show why it matters. */
  smoothPayoff?: boolean;
}

export interface FdSolution {
  /** Spot price at each grid node (log-spaced). */
  spots: Float64Array;
  /** Option values today (tau = T) at each node. */
  values: Float64Array;
  /** Price at the input spot (the middle node). */
  price: number;
  /** Values at requested times to expiry, oldest-first: payoff (tau = 0) then each target. */
  snapshots: { tau: number; values: Float64Array }[];
}

export const DEFAULT_FD_GRID: Readonly<FdGridOptions> = {
  nSpace: 801,
  nTime: 400,
  widthStdDevs: 6,
  rannacherSteps: 2,
};

/** Solve a x_{i-1} + b x_i + c x_{i+1} = d in place (Thomas). Arrays length n. */
function thomas(
  a: Float64Array,
  b: Float64Array,
  c: Float64Array,
  d: Float64Array,
  n: number,
  cp: Float64Array,
): void {
  cp[0] = (c[0] as number) / (b[0] as number);
  d[0] = (d[0] as number) / (b[0] as number);
  for (let i = 1; i < n; i++) {
    const m = (b[i] as number) - (a[i] as number) * (cp[i - 1] as number);
    cp[i] = (c[i] as number) / m;
    d[i] = ((d[i] as number) - (a[i] as number) * (d[i - 1] as number)) / m;
  }
  for (let i = n - 2; i >= 0; i--)
    d[i] = (d[i] as number) - (cp[i] as number) * (d[i + 1] as number);
}

export function fdPrice(
  inputs: BsInputs,
  kind: 'call' | 'put',
  grid: FdGridOptions = DEFAULT_FD_GRID,
): number {
  const { spot: S, strike: K, rate: r, vol: sigma, timeYears: T } = inputs;
  const q = inputs.dividendYield ?? 0;
  if (T <= 0 || sigma <= 0) {
    const fwdS = S * Math.exp(-q * T);
    const pvK = K * Math.exp(-r * T);
    return Math.max(kind === 'call' ? fwdS - pvK : pvK - fwdS, 0);
  }
  return fdSolve(inputs, kind, grid).price;
}

/**
 * Full solve: the whole grid today, plus snapshots of V(S) at the
 * requested times to expiry (each recorded at the first step reaching it).
 * Requires T > 0 and sigma > 0.
 */
export function fdSolve(
  inputs: BsInputs,
  kind: 'call' | 'put',
  grid: FdGridOptions = DEFAULT_FD_GRID,
  snapshotTaus: number[] = [],
): FdSolution {
  const { spot: S, strike: K, rate: r, vol: sigma, timeYears: T } = inputs;
  const q = inputs.dividendYield ?? 0;
  if (grid.nSpace % 2 === 0 || grid.nSpace < 5)
    throw new RangeError('nSpace must be odd and at least 5');
  if (!(T > 0) || !(sigma > 0)) throw new RangeError('fdSolve needs T > 0 and sigma > 0');

  const n = grid.nSpace;
  const mid = (n - 1) / 2;
  const x0 = Math.log(S);
  const halfWidth = grid.widthStdDevs * sigma * Math.sqrt(T);
  const dx = halfWidth / mid;
  const mu = r - q - 0.5 * sigma * sigma;

  // Operator A: (A V)_i = lo V_{i-1} + di V_i + up V_{i+1}
  const s2 = (sigma * sigma) / (dx * dx);
  const lo = 0.5 * s2 - mu / (2 * dx);
  const di = -s2 - r;
  const up = 0.5 * s2 + mu / (2 * dx);

  const V = new Float64Array(n);
  const rhs = new Float64Array(n);
  const a = new Float64Array(n);
  const b = new Float64Array(n);
  const c = new Float64Array(n);
  const cp = new Float64Array(n);
  const sAt = new Float64Array(n);
  for (let i = 0; i < n; i++) {
    const s = Math.exp(x0 + (i - mid) * dx);
    sAt[i] = s;
    V[i] = Math.max(kind === 'call' ? s - K : K - s, 0);
  }
  // Cell-average the payoff over the one cell whose interior contains ln K.
  const lnK = Math.log(K);
  const kCell = Math.round((lnK - x0) / dx + mid);
  if (grid.smoothPayoff !== false && kCell > 0 && kCell < n - 1) {
    const xa = x0 + (kCell - mid - 0.5) * dx;
    const xb = xa + dx;
    if (kind === 'call') {
      const lo = Math.max(xa, lnK);
      V[kCell] = xb > lo ? (Math.exp(xb) - Math.exp(lo) - K * (xb - lo)) / dx : 0;
    } else {
      const hi = Math.min(xb, lnK);
      V[kCell] = hi > xa ? (K * (hi - xa) - (Math.exp(hi) - Math.exp(xa))) / dx : 0;
    }
  }
  const sMin = sAt[0] as number;
  const sMax = sAt[n - 1] as number;

  function boundaries(tau: number): [number, number] {
    const dS = Math.exp(-q * tau);
    const dK = Math.exp(-r * tau);
    return kind === 'call'
      ? [0, Math.max(sMax * dS - K * dK, 0)]
      : [Math.max(K * dK - sMin * dS, 0), 0];
  }

  function step(theta: number, dt: number, tauNext: number): void {
    // Right-hand side: (I + (1 - theta) dt A) V on interior nodes.
    const e = (1 - theta) * dt;
    for (let i = 1; i < n - 1; i++) {
      rhs[i] =
        (V[i] as number) +
        e * (lo * (V[i - 1] as number) + di * (V[i] as number) + up * (V[i + 1] as number));
    }
    // Left-hand side: (I - theta dt A). Boundary rows are identity (Dirichlet).
    const f = theta * dt;
    const [vLo, vHi] = boundaries(tauNext);
    a[0] = 0;
    b[0] = 1;
    c[0] = 0;
    rhs[0] = vLo;
    a[n - 1] = 0;
    b[n - 1] = 1;
    c[n - 1] = 0;
    rhs[n - 1] = vHi;
    for (let i = 1; i < n - 1; i++) {
      a[i] = -f * lo;
      b[i] = 1 - f * di;
      c[i] = -f * up;
    }
    thomas(a, b, c, rhs, n, cp);
    V.set(rhs);
  }

  const snapshots: { tau: number; values: Float64Array }[] = [{ tau: 0, values: V.slice() }];
  const targets = [...snapshotTaus].filter((t) => t > 0 && t <= T).sort((x, y) => x - y);
  let nextTarget = 0;
  function record(tauNow: number): void {
    while (nextTarget < targets.length && tauNow >= (targets[nextTarget] as number) - 1e-12) {
      snapshots.push({ tau: tauNow, values: V.slice() });
      nextTarget++;
    }
  }

  const dt = T / grid.nTime;
  let tau = 0;
  const r0 = Math.min(grid.rannacherSteps, grid.nTime);
  for (let k = 0; k < 2 * r0; k++) {
    tau += dt / 2;
    step(1, dt / 2, tau);
    record(tau);
  }
  for (let k = r0; k < grid.nTime; k++) {
    tau += dt;
    step(0.5, dt, tau);
    record(tau);
  }
  return { spots: sAt, values: V, price: V[mid] as number, snapshots };
}
